import React from 'react'
import { Box, Checkbox } from '@mui/material'
import type { CheckboxProps } from '@mui/material'

/**
 * The ONE multi-select checkbox for the certificates surface.
 *
 * Extracted because it was hand-rolled twice — once on the card, once in the
 * selection toolbar — and the two copies had drifted into different sizes (20 vs
 * 24) and different corner radii (6 vs 4) for the same control in the same mode.
 * That drift is exactly what made the toolbar's read as "too big and too dark"
 * next to the cards it governs: nothing was individually wrong, they just did not
 * agree, and the bigger of the two was the one sitting at the top of the screen.
 *
 * ## Why the border is `!important`
 *
 * `index.css` carries `#vera-shadow-root * { border-color: hsl(var(--border)) }`.
 * Its ID specificity (0-1-0-0) beats emotion's single-class `sx` output
 * (0-0-1-0), so a plain `border` here is overwritten and the box renders in the
 * shared border grey — near-invisible, on the one control that turns the whole
 * selection mode on. `!important` is what wins; the property form (shorthand vs
 * `borderColor`) is irrelevant.
 *
 * ## Weight
 *
 * 1.5px at zinc-400 rather than 2px at grey[600]. An unchecked checkbox is an
 * INVITATION, not a value — it should be legible without being the heaviest ink
 * on a card whose subject is a photograph. The checked state is a solid black
 * fill, so the contrast between "off" and "on" stays unmistakable even though the
 * resting outline got lighter.
 */
export const SelectCheckbox: React.FC<CheckboxProps> = props => {
  const box = {
    width: SELECT_CHECKBOX_SIZE,
    height: SELECT_CHECKBOX_SIZE,
    borderRadius: '6px',
  } as const

  const filled = {
    ...box,
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const

  return (
    <Checkbox
      size='small'
      disableRipple
      {...props}
      sx={{
        p: 0,
        '& .MuiSvgIcon-root': { fontSize: SELECT_CHECKBOX_SIZE },
        '& .MuiTouchRipple-root': { display: 'none' },
        '&:hover': { backgroundColor: 'transparent' },
        ...props.sx,
      }}
      icon={<Box sx={{ ...box, border: '1.5px solid #A1A1AA !important' }} />}
      checkedIcon={
        <Box sx={filled}>
          <Box
            component='span'
            sx={{
              width: 10,
              height: 6,
              borderBottom: '2px solid #fff',
              borderLeft: '2px solid #fff',
              transform: 'rotate(-45deg)',
              mt: '-1px',
            }}
          />
        </Box>
      }
      indeterminateIcon={
        <Box sx={filled}>
          <Box component='span' sx={{ width: 10, height: 2, backgroundColor: '#fff', borderRadius: '8px' }} />
        </Box>
      }
    />
  )
}

/** Both call sites size their tap target from this, so they cannot drift again. */
export const SELECT_CHECKBOX_SIZE = 20

export default SelectCheckbox
